import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTr } from "@/i18n/translator";

const NETS = ["twitter", "facebook", "instagram", "linkedin", "tiktok"];
const NET_COLOR = {
  twitter: "bg-black text-white",
  facebook: "bg-[#1877F2] text-white",
  instagram: "bg-[#E4405F] text-white",
  linkedin: "bg-[#0A66C2] text-white",
  tiktok: "bg-[#0A0A0A] text-[#FFD600]",
};

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function daysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export default function CalendarPage() {
  const tr = useTr();
  const { i18n } = useTranslation();
  const locale = ({ es: "es-MX", en: "en-US", pt: "pt-BR", fr: "fr-FR", de: "de-DE", it: "it-IT" })[(i18n.language || "es").slice(0, 2)] || "en-US";
  const [posts, setPosts] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null); // {day}
  const [dragging, setDragging] = useState(null);

  const load = () => api.get("/calendar").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const grid = useMemo(() => {
    const first = startOfMonth(month);
    const total = daysInMonth(month);
    const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const postsByDay = useMemo(() => {
    const m = {};
    for (const p of posts) {
      if (!p.scheduled_at) continue;
      const d = new Date(p.scheduled_at);
      if (d.getMonth() !== month.getMonth() || d.getFullYear() !== month.getFullYear()) continue;
      const key = d.getDate();
      m[key] = m[key] || [];
      m[key].push(p);
    }
    return m;
  }, [posts, month]);

  const moveTo = async (postId, newDate) => {
    const p = posts.find((x) => x.id === postId);
    if (!p) return;
    const old = new Date(p.scheduled_at);
    const updated = new Date(newDate);
    updated.setHours(old.getHours(), old.getMinutes(), 0, 0);
    try {
      await api.patch(`/posts/${postId}`, {
        ...p,
        scheduled_at: updated.toISOString(),
      });
      toast.success(tr("Reprogramado"), { description: updated.toLocaleString(locale) });
      load();
    } catch (e) {
      toast.error("No se pudo mover");
    }
  };

  const approve = async (p) => {
    await api.patch(`/posts/${p.id}/status`, { status: "scheduled" });
    toast.success("Post programado");
    load();
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("DESPLIEGUE · CRONOGRAMA")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">
            {month.toLocaleString(locale, { month: "long", year: "numeric" })}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="border border-[#0A0A0A] p-2 hover:bg-[#0A0A0A] hover:text-white" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button className="border border-[#0A0A0A] p-2 hover:bg-[#0A0A0A] hover:text-white" onClick={() => setMonth(new Date())} data-testid="cal-today">
            <span className="font-mono-mark text-[10px] px-2">{tr("HOY")}</span>
          </button>
          <button className="border border-[#0A0A0A] p-2 hover:bg-[#0A0A0A] hover:text-white" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="border border-[#0A0A0A] bg-white">
        <div className="grid grid-cols-7 border-b border-[#0A0A0A]">
          {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map((d) => (
            <div key={d} className="p-3 font-mono-mark text-[10px] text-[#5C5C5C] border-r last:border-r-0 border-[#0A0A0A]">{tr(d)}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const list = d ? postsByDay[d.getDate()] || [] : [];
            const isToday = d && new Date().toDateString() === d.toDateString();
            return (
              <div
                key={i}
                onDragOver={(e) => { if (dragging) e.preventDefault(); }}
                onDrop={() => { if (dragging && d) moveTo(dragging, d); setDragging(null); }}
                className={`min-h-[120px] border-b border-r border-[#0A0A0A] p-2 last:border-r-0 ${d ? "" : "bg-[#F4F4F0]"}`}
              >
                {d && (
                  <>
                    <div className={`font-mono-data text-xs mb-1 ${isToday ? "bg-[#E53935] text-white px-1 inline-block" : "text-[#5C5C5C]"}`}>
                      {d.getDate()}
                    </div>
                    <div className="space-y-1">
                      {list.map((p) => (
                        <button
                          draggable
                          onDragStart={() => setDragging(p.id)}
                          onClick={() => setSelected(p)}
                          key={p.id}
                          className={`w-full text-left font-mono-mark text-[9px] px-2 py-1 truncate ${NET_COLOR[p.network]} hover:opacity-80`}
                          data-testid={`cal-post-${p.id}`}
                        >
                          {p.network.toUpperCase()} · {p.body.slice(0, 24)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white border border-[#0A0A0A] brutal-shadow max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#0A0A0A] pb-3 mb-4">
              <span className={`font-mono-mark text-[10px] px-2 py-1 ${NET_COLOR[selected.network]}`}>{selected.network.toUpperCase()}</span>
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{selected.status.toUpperCase()}</span>
            </div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PROGRAMADO")}</div>
            <div className="font-mono-data text-sm">{new Date(selected.scheduled_at).toLocaleString(locale)}</div>
            <p className="text-sm mt-4 whitespace-pre-wrap">{selected.body}</p>
            {selected.video_url && (
              <video src={selected.video_url} controls className="w-full max-h-80 bg-black mt-3 border border-[#0A0A0A]" />
            )}
            {selected.hashtags?.length > 0 && (
              <div className="mt-3 text-sm text-[#E53935] font-mono-data">{selected.hashtags.join(" ")}</div>
            )}
            <div className="mt-6 flex gap-3 justify-end">
              {selected.status === "pending" && (
                <button onClick={() => { approve(selected); setSelected(null); }} className="font-mono-mark text-[10px] bg-[#E53935] text-white px-4 py-2 border border-[#E53935]" data-testid="cal-approve">
                  {tr("APROBAR Y PROGRAMAR")}
                </button>
              )}
              <button onClick={() => setSelected(null)} className="font-mono-mark text-[10px] bg-white text-[#0A0A0A] px-4 py-2 border border-[#0A0A0A]">{tr("CERRAR")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
